import {
  getCommercial,
  getPendingSales,
  saveSaleFigure,
} from 'portal-modules/salesinformation/api/api'
import { all, call, put, takeLatest, takeEvery } from 'redux-saga/effects'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'

const initialState = {
  pending: {},
  submitted: {},
  posted: {},
  filter: {},
}

// reducer
export default (state = initialState, { payload, type }) => {
  switch (type) {
    case RECEIVE_PENDING_SALES:
      return { ...state, ...payload }
    case EDIT_SALE_FIGURES:
      return { ...state, sale_figure_id: payload }
    case SALE_FIGURE_UPDATED:
      return {
        ...state,
        [payload.id]: payload,
      }
    case SAVE_COMMERCIAL_FILTER:
      return {
        ...state,
        filter: payload,
      }
    case CLEAR_COMMERCIAL_FILTER:
      return {
        ...state,
        filter: {},
      }
    default:
      return state
  }
}

// action type
export const REQUEST_COMMERCIAL = 'tp/suite/REQUEST_COMMERCIAL'
const REQUEST_PENDING_SALES = 'rp/sales_figures/REQUEST_PENDING_SALES'
const RECEIVE_PENDING_SALES = 'rp/sales_figures/RECEIVE_PENDING_SALES'
const EDIT_SALE_FIGURES = 'rp/sale-figures/EDIT_SALE_FIGURES'
const UPDATE_SALE_FIGURES = 'rp/sale-figures/UPDATE_SALE_FIGURES'
const SALE_FIGURE_UPDATED = 'rp/sale-figures/SALE_FIGURE_UPDATED'
const SAVE_COMMERCIAL_FILTER = 'rp/sale-figures/SAVE_COMMERCIAL_FILTER'
const CLEAR_COMMERCIAL_FILTER = 'rp/sale-figures/CLEAR_COMMERCIAL_FILTER'

// action creator
export const requestCommercial = createFormAction(REQUEST_COMMERCIAL)

export const updateSaleFigure = createFormAction(UPDATE_SALE_FIGURES)

export const requestPendingSales = () => ({ type: REQUEST_PENDING_SALES })

export const receivePendingSales = payload => ({
  type: RECEIVE_PENDING_SALES,
  payload,
})

export const editSaleFigure = payload => ({
  type: EDIT_SALE_FIGURES,
  payload,
})

export const saveCommercialFilter = payload => ({
  type: SAVE_COMMERCIAL_FILTER,
  payload,
})

export const clearCommercialFilter = () => ({ type: CLEAR_COMMERCIAL_FILTER })

/**
 * Parses the filter's date boundaries into epoch timestamps.
 * Returns 0 when a boundary is not set (meaning "unbounded").
 */
const parseFilterDateRange = (filter) => ({
  startDateTime: filter.startDate ? new Date(filter.startDate).getTime() : 0,
  endDateTime: filter.endDate ? new Date(filter.endDate).getTime() : 0,
})

/**
 * Computes the start / end timestamps for a single sale-figure item.
 * Handles both single-month periods and title-based date ranges
 * (e.g. "Sales Figures Jan 2023 - Mar 2023").
 */
const parseSaleFigureDateRange = (obj) => {
  const saleFigureDate = new Date(obj.measure_period)
  let startTime = saleFigureDate.getTime()
  let endTime = new Date(
    saleFigureDate.getFullYear(),
    saleFigureDate.getMonth() + 1,
    0,
  ).getTime()

  if (obj.title.indexOf('-') > -1) {
    const titleArr = obj.title.toString().replace('Sales Figures', '').split('-')
    startTime = new Date(titleArr[0].toString().trim()).getTime()
    endTime = new Date(titleArr[1].toString().trim()).getTime()
  }

  return { startTime, endTime }
}

/**
 * Returns true when the item's amount falls within the filter's price range.
 */
const matchesPriceFilter = (amount, filter, skipPrice) => {
  if (skipPrice) return true
  if (filter.fromPrice && filter.fromPrice > amount) return false
  if (filter.toPrice && amount > filter.toPrice) return false
  return true
}

/**
 * Returns true when the item's date range overlaps with the filter's date range.
 */
const matchesDateFilter = (startTime, endTime, startDateTime, endDateTime) => {
  if (startDateTime && endTime < startDateTime) return false
  if (endDateTime && startTime > endDateTime) return false
  return true
}

export const applyFilter = (filter, submitted, skipPrice = false) => {
  if (!Array.isArray(submitted)) return []
  if (!Object.keys(filter).length) return submitted

  const { startDateTime, endDateTime } = parseFilterDateRange(filter)

  return submitted.filter(obj => {
    if (!matchesPriceFilter(obj.amount, filter, skipPrice)) return false
    const { startTime, endTime } = parseSaleFigureDateRange(obj)
    return matchesDateFilter(startTime, endTime, startDateTime, endDateTime)
  })
}

// sagas

export function* fetchCommercial() {
  yield call(getCommercial)
}

export function* fetchPendingSales() {
  const pendingSales = yield call(getPendingSales)
  let actions = []
  actions.push(receivePendingSales(pendingSales))
  yield all(actions.map(a => put(a)))
}

function* addOrUpdateSaleFigure({ payload: { document, ...rest }, type }) {
  const formData = new FormData()

  Object.keys(rest).map(key => formData.append(key, rest[key]))

  undefined != document &&
    document.map(docFile => {
      formData.append(`document`, docFile)
    })

  const { saleFigure } = yield call(saveSaleFigure, formData)
  if (saleFigure && type === UPDATE_SALE_FIGURES) {
    yield put({ type: SALE_FIGURE_UPDATED, payload: saleFigure })
  }
}

export function* watchRequestCommercial() {
  yield takeLatest(
    REQUEST_COMMERCIAL,
    createRequestSaga(fetchCommercial, {
      key: 'commercial',
      message: __('Looking up your commercial'),
    }),
  )
}

export function* watchPendingSales() {
  yield takeLatest(
    REQUEST_PENDING_SALES,
    createRequestSaga(fetchPendingSales, {
      key: 'commercial',
      message: __('Looking for pending Sales'),
    }),
  )
}

function* watchUpdateSaleFigure() {
  yield takeEvery(
    [UPDATE_SALE_FIGURES],
    createRequestSaga(addOrUpdateSaleFigure, {
      key: 'commercial',
      message: __('Syncing updates'),
      successMessage: __('Sales Figures saved'),
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestCommercial,
  watchPendingSales,
  watchUpdateSaleFigure,
]
